import { useViewportResize } from '@resn/gozer-vue';

/** Release gesture: recent-window swipe in vmin units. */
export const SWIPE_DEFAULTS = {
    swipeWindowMs: 250,
    swipeDistanceVmin: 0.15,
    swipeVelocityVminMs: 0.00175,
    idleSpeedVminMs: 0.00015,
    /** No pointer events for this long = pause (still finger down). */
    idleGapMs: 80,
};

const NEAR_ZERO = 0.01;

const segLength = (dx, dy, axis) => {
    if (axis === 'x') return Math.abs(dx);
    if (axis === 'y') return Math.abs(dy);
    return Math.hypot(dx, dy);
};

/**
 * Tracks pointer samples and evaluates whether a release gesture should advance.
 *
 * Over the last `swipeWindowMs`, a swipe requires on the current motion stroke:
 * - net travel since the last direction change or pause > `swipeDistanceVmin`
 * - average speed during that stroke > `swipeVelocityVminMs`
 * Pauses are detected via low segment speed or a gap between pointer samples.
 */
export const useSwipeAdvance = (options = {}) => {
    const {
        swipeWindowMs = SWIPE_DEFAULTS.swipeWindowMs,
        swipeDistanceVmin = SWIPE_DEFAULTS.swipeDistanceVmin,
        swipeVelocityVminMs = SWIPE_DEFAULTS.swipeVelocityVminMs,
        idleSpeedVminMs = SWIPE_DEFAULTS.idleSpeedVminMs,
        idleGapMs = SWIPE_DEFAULTS.idleGapMs,
    } = options;

    const viewport = useViewportResize();
    const pxToVmin = (px) => px / (Math.min(viewport.width, viewport.height) || 375);

    const pointerSamples = [];

    const trimSamples = (t) => {
        const cutoff = t - swipeWindowMs;
        let i = 0;
        while (i < pointerSamples.length && pointerSamples[i].t < cutoff) i++;
        if (i > 0) pointerSamples.splice(0, i);
    };

    const recordPointerSample = (x, y) => {
        const t = performance.now();
        pointerSamples.push({ x, y, t });
        trimSamples(t);
    };

    const markDragStart = () => {};

    const resetPointerTracking = (x, y) => {
        pointerSamples.length = 0;
        recordPointerSample(x, y);
    };

    const clearPointerSamples = () => {
        pointerSamples.length = 0;
    };

    /** @param {'x' | 'y' | 'both'} [axis] */
    const evaluateSwipe = (axis = 'both') => {
        const samples = pointerSamples;
        if (samples.length < 2) {
            return { isSwipe: false, dx: 0, dy: 0 };
        }

        const now = performance.now();
        const lastSample = samples[samples.length - 1];

        // Finger held still — no pointer events — before release.
        if (now - lastSample.t > idleGapMs) {
            return { isSwipe: false, dx: 0, dy: 0 };
        }

        let breakIndex = 0;
        let isIdle = true;
        let prevSign = 0;

        for (let i = 1; i < samples.length; i++) {
            const prev = samples[i - 1];
            const curr = samples[i];
            const dt = curr.t - prev.t;
            if (dt <= 0) continue;

            if (dt > idleGapMs) {
                if (!isIdle) breakIndex = i - 1;
                isIdle = true;
                prevSign = 0;
                continue;
            }

            const dx = curr.x - prev.x;
            const dy = curr.y - prev.y;
            const len = segLength(dx, dy, axis);
            const speed = pxToVmin(len) / dt;

            if (speed < idleSpeedVminMs) {
                if (!isIdle) breakIndex = i - 1;
                isIdle = true;
                continue;
            }

            const sign =
                Math.abs(dx) >= Math.abs(dy) ? Math.sign(dx) || prevSign : Math.sign(dy) || prevSign;

            if (isIdle || (prevSign !== 0 && sign !== 0 && sign !== prevSign)) {
                breakIndex = i - 1;
            }

            if (sign !== 0) prevSign = sign;
            isIdle = false;
        }

        if (isIdle) breakIndex = samples.length - 1;

        const start = samples[breakIndex];
        const end = samples[samples.length - 1];
        const netDx = end.x - start.x;
        const netDy = end.y - start.y;
        const distance = pxToVmin(segLength(netDx, netDy, axis));

        let pathVmin = 0;
        let activeMs = 0;

        for (let i = Math.max(breakIndex + 1, 1); i < samples.length; i++) {
            const prev = samples[i - 1];
            const curr = samples[i];
            const dt = curr.t - prev.t;
            if (dt <= 0) continue;

            if (dt > idleGapMs) continue;

            const dx = curr.x - prev.x;
            const dy = curr.y - prev.y;
            const len = segLength(dx, dy, axis);
            const speed = pxToVmin(len) / dt;

            if (speed < idleSpeedVminMs) continue;

            pathVmin += pxToVmin(len);
            activeMs += dt;
        }

        const velocity = activeMs > 0 ? pathVmin / activeMs : 0;
        const isSwipe = distance > swipeDistanceVmin && velocity > swipeVelocityVminMs;

        return {
            isSwipe,
            dx: axis === 'y' ? 0 : netDx,
            dy: axis === 'x' ? 0 : netDy,
        };
    };

    /** @param {'x' | 'y' | 'both'} [axis] @param {{ flipLeaveY?: boolean }} [options] */
    const getShouldAdvance = (axis = 'both', { flipLeaveY = false } = {}) => {
        const { isSwipe, dx, dy } = evaluateSwipe(axis);

        let leaveDirection = { x: -1, y: 0 };
        const leaveY = flipLeaveY ? -dy : dy;
        const len = Math.hypot(dx, leaveY);

        if (len > NEAR_ZERO) {
            leaveDirection = { x: dx / len, y: leaveY / len };
        }

        return {
            shouldAdvance: isSwipe,
            sign: {
                x: Math.sign(axis === 'y' ? 1 : dx || 1),
                y: Math.sign(axis === 'x' ? 1 : dy || 1),
            },
            leaveDirection,
        };
    };

    return {
        markDragStart,
        resetPointerTracking,
        clearPointerSamples,
        recordPointerSample,
        getShouldAdvance,
    };
};
